import { useState, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { RippleButton } from "./RippleButton";
import { Blobs, FloatingBubbles, PawPrint } from "./Effects";
import { Star, MapPin, Clock } from "lucide-react";

export default function Hero({ onBook }) {
  const ref = useRef(null);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 600], [0, 80]);

  const onMove = (e) => {
    const { innerWidth: w, innerHeight: h } = window;
    setMouse({ x: (e.clientX - w / 2) / 40, y: (e.clientY - h / 2) / 40 });
  };

  return (
    <section ref={ref} onMouseMove={onMove} data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-28 sm:pt-36 lg:pt-44 pb-12 sm:pb-20 overflow-hidden">
      <Blobs />
      <FloatingBubbles count={12} />
      {/* floating paws */}
      {[["12%", "22%", 0], ["82%", "30%", 1.5], ["70%", "70%", 3]].map(([l, t, d], i) => (
        <motion.div key={i} className="absolute text-woofsy-primary/20 w-10 h-10"
          style={{ left: l, top: t }} animate={{ y: [0, -20, 0], rotate: [0, 10, 0] }}
          transition={{ duration: 6, delay: d, repeat: Infinity }}>
          <PawPrint className="w-10 h-10" />
        </motion.div>
      ))}

      <div className="relative max-w-7xl mx-auto w-full px-5 sm:px-8 grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        {/* Left copy */}
        <motion.div style={{ x: mouse.x * -0.4 }}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="inline-flex items-center gap-2 glass rounded-full px-4 py-2 mb-6 text-sm font-medium text-woofsy-dark">
            <span className="flex h-2 w-2 rounded-full bg-woofsy-accent animate-pulse" />
            Less Waiting . More Wagging • Pune, India
          </motion.div>

          <h1 className="font-heading font-extrabold text-3xl sm:text-5xl lg:text-6xl leading-[1.15] text-woofsy-dark">
            Pet Grooming Services
            <span className="block mt-1 sm:mt-2">
              <span className="text-gradient">In Just </span>
              <span className="relative inline-block">
                <span className="text-gradient">30 Min.</span>
                <motion.span className="absolute -bottom-2 left-0 h-3 w-full bg-woofsy-accent/60 -z-10 rounded-full"
                  initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.8, duration: 0.6 }} style={{ originX: 0 }} />
              </span>
            </span>
          </h1>

          <p className="mt-6 text-lg text-woofsy-dark/70 max-w-md">
            <strong className="text-woofsy-primary font-semibold block mb-1">Less Waiting . More Wagging</strong>
            Stress-free grooming for your dogs by trained professionals at your doorstep in Pune.
          </p>

          <div className="mt-6 sm:mt-8 flex flex-wrap gap-3 sm:gap-4">
            <RippleButton data-testid="hero-book-btn" variant="primary" className="px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base" onClick={onBook}>Book Grooming</RippleButton>
            <RippleButton data-testid="hero-how-btn" variant="ghost" className="px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base"
              onClick={() => document.getElementById("how")?.scrollIntoView({ behavior: "smooth" })}>How It Works</RippleButton>
          </div>

          <div className="mt-6 sm:mt-10 flex flex-wrap gap-4 sm:gap-6 text-xs sm:text-sm text-woofsy-dark/70">
            <span className="flex items-center gap-2"><Star className="w-4 h-4 fill-woofsy-primary text-woofsy-primary" /> 4.9 rating</span>
            <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-woofsy-primary" /> 30 min arrival</span>
            <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-woofsy-primary" /> At your doorstep</span>
          </div>
        </motion.div>

        {/* Right illustration */}
        <motion.div style={{ x: mouse.x, y: mouse.y }} className="relative mt-6 lg:mt-0">
          <motion.div style={{ y: yImg }}
            initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3, type: "spring" }}
            className="relative">
            <div className="relative rounded-[2rem] sm:rounded-[2.5rem] overflow-hidden glass p-2 sm:p-3 shadow-[0_30px_80px_rgba(255,138,61,0.25)]">
              <img data-testid="hero-image" loading="eager" src="/Woofsy - Landing Page.png"
                alt="Freshly groomed happy dog mobile spa" className="rounded-[1.5rem] sm:rounded-[2rem] w-full h-[280px] sm:h-[380px] lg:h-[480px] object-cover object-center" />
            </div>

            {/* floating stat cards */}
            <motion.div animate={{ y: [0, -12, 0] }} transition={{ duration: 4, repeat: Infinity }}
              className="absolute -left-3 sm:-left-8 top-6 sm:top-10 glass rounded-xl sm:rounded-2xl px-3 sm:px-4 py-2 sm:py-3 shadow-xl">
              <p className="text-lg sm:text-2xl font-heading font-bold text-woofsy-primary">5000+</p>
              <p className="text-[10px] sm:text-xs text-woofsy-dark/60">Happy Pets</p>
            </motion.div>
            <motion.div animate={{ y: [0, 12, 0] }} transition={{ duration: 5, repeat: Infinity }}
              className="absolute -right-2 sm:-right-6 bottom-10 sm:bottom-16 glass rounded-xl sm:rounded-2xl px-3 sm:px-4 py-2 sm:py-3 shadow-xl flex items-center gap-2">
              <span className="text-lg sm:text-2xl">✅</span>
              <div><p className="text-xs sm:text-sm font-heading font-bold text-woofsy-dark">Verified</p><p className="text-[10px] sm:text-xs text-woofsy-dark/60">Groomers</p></div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>


    </section>
  );
}
